// packages/core/src/sage-quota.ts
//
// Pure, dependency-free helpers for the Sage daily fair-use quota. Imported by
// server routes (to emit the remaining-count headers), by web client components
// (to read them + render the near-limit notice), and by the PHONE.
//
// It lives in packages/core rather than src/lib/tutor for that last reason: the
// mobile app cannot import from src/, so while this sat there the app simply
// ignored the headers — a Free student discovered the 3/day cap by hitting an
// error, which reads as breakage rather than as a tier.
//
// Packaging v2 meters Sage by tier (FREE 3/day taste, PRO 50/day fair-use,
// ADMIN unlimited — see TUTOR_DAILY_CAP). This surfaces the remaining count so
// a user isn't surprised by a 429: they get a quiet "N left today" as they
// approach the cap, and a friendly resets-tomorrow message when they hit it.

/** Response header carrying the user's remaining Sage messages for today. */
export const SAGE_REMAINING_HEADER = 'X-Sage-Daily-Remaining'
/** Response header carrying the user's daily Sage cap (per tier). */
export const SAGE_CAP_HEADER = 'X-Sage-Daily-Cap'

/**
 * Show the heads-up once the user is within this many messages of the cap.
 * FREE's cap is 3, so its users effectively always see the count (appropriate
 * for a "taste"); PRO (50) only sees it in the final stretch.
 */
export const SAGE_NEAR_LIMIT_THRESHOLD = 5

/** The shape checkTutorRateLimit returns for the quota fields. */
export type SageQuota = { dailyRemaining?: number; dailyCap?: number }

/**
 * Build the response headers for a successful Sage call. Returns an empty
 * object when the values are absent (e.g. ADMINs, who are uncapped) so the
 * client simply renders no notice.
 */
export function sageQuotaHeaders(q: SageQuota): Record<string, string> {
  const headers: Record<string, string> = {}
  if (typeof q.dailyRemaining === 'number' && Number.isFinite(q.dailyRemaining)) {
    headers[SAGE_REMAINING_HEADER] = String(q.dailyRemaining)
  }
  if (typeof q.dailyCap === 'number' && Number.isFinite(q.dailyCap)) {
    headers[SAGE_CAP_HEADER] = String(q.dailyCap)
  }
  return headers
}

/** Parse the remaining/cap headers off a fetch Response. `null` when absent. */
export function parseSageQuotaHeaders(headers: Headers): {
  remaining: number | null
  cap: number | null
} {
  const num = (raw: string | null): number | null => {
    if (raw == null || raw === '') return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  }
  return {
    remaining: num(headers.get(SAGE_REMAINING_HEADER)),
    cap: num(headers.get(SAGE_CAP_HEADER)),
  }
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * When the daily allowance comes back, as a phrase: "in about 5 hours".
 *
 * The day window in rate-limit-db.ts is epoch-aligned — it resets at 00:00
 * UTC, which for a student in New York is 8 p.m., not "tomorrow". The copy
 * said "resets tomorrow" anyway (build-7 audit, finding 9). Because the window
 * is a fixed UTC day, the real reset can be computed anywhere from the clock
 * alone, and a duration is right in every timezone without knowing which one
 * the student is in.
 */
export function sageDailyResetPhrase(nowMs: number = Date.now()): string {
  const untilMs = Math.ceil(nowMs / DAY_MS) * DAY_MS - nowMs || DAY_MS
  const minutes = Math.ceil(untilMs / 60_000)
  if (minutes <= 1) return 'in a minute'
  if (minutes < 60) return `in ${minutes} minutes`
  const hours = Math.round(untilMs / 3_600_000)
  return hours <= 1 ? 'in about an hour' : `in about ${hours} hours`
}

/**
 * The near-limit notice string, or `null` when there's nothing to say (unknown
 * remaining, or comfortably above the threshold). Honest "fair-use" framing —
 * never "unlimited".
 */
export function sageNearLimitNotice(
  remaining: number | null,
  cap: number | null,
  nowMs: number = Date.now(),
): string | null {
  if (remaining == null || !Number.isFinite(remaining)) return null
  if (remaining > SAGE_NEAR_LIMIT_THRESHOLD) return null
  if (remaining <= 0) {
    return `That was your last Sage message for now — your daily fair-use limit resets ${sageDailyResetPhrase(nowMs)}.`
  }
  const count = remaining === 1 ? '1 Sage message' : `${remaining} Sage messages`
  const capSuffix = typeof cap === 'number' && Number.isFinite(cap) ? ` of ${cap}` : ''
  return `${count} left today (daily fair-use limit${capSuffix}).`
}

/**
 * Friendly copy for a daily-cap 429 (blockedBy === 'day'). The generic
 * "try again in Ns" reads badly when N is most of a day, so callers should
 * use this for the day window.
 */
export function sageDailyLimitMessage(nowMs: number = Date.now()): string {
  return `You've used all your Sage messages for now (daily fair-use limit). It resets ${sageDailyResetPhrase(nowMs)}.`
}
